import {useRef, useState, useEffect, useCallback} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';

import {usePathname} from 'src/routes/hooks';
import {useActiveLink} from 'src/routes/hooks/use-active-link';

import NavItem from './nav-item';
import {NavListProps, NavSubListProps} from '../types';
import {
  BASE_LEGACY_APP,
  BASE_MENU_ITEM,
  BASE_RECORD_ID,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  BASE_ROUTE_SUB_MODULE,
  BASE_SPECIMEN_NUMBER,
  BASE_SPECIMEN_NUMBER_ID,
  BASE_SPECIMEN_TYPE,
  BASE_UNIQUE_ID,
  IS_FROM_EPIC,
  IS_FROM_ATHENA,
  MODE,
  PARAMS_KEY,
  BASE_ACCESSION_NUMBER
} from 'src/routes/BaseRoute';
import EncryptUtils from 'src/utils/EncryptUtils';

// ----------------------------------------------------------------------

export default function SubModuleNavList({
  data,
  depth,
  slotProps,
  url,
  subListPath,
  moduleName
}: NavListProps) {
  const navRef = useRef<HTMLDivElement | null>(null);

  const pathname = usePathname();
  const location = useLocation();
  const params: any = useParams();

  const recordId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const legacyApp = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_LEGACY_APP
  );

  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_UNIQUE_ID
  );

  const isFromEpic = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_EPIC
  );

  const isFromAthena = EncryptUtils.getDecryptedData(
    location?.search,
    IS_FROM_ATHENA
  );

  const specimen_number = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_NUMBER
  );

  const specimen_number_id = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_NUMBER_ID
  );
 const accessionNumber = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_ACCESSION_NUMBER
  );
  const specimen_type = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_SPECIMEN_TYPE
  );

  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);

  const mode = EncryptUtils.getDecryptedData(location?.search, MODE);

  const paramsKey = EncryptUtils.setEncryptedParams({
    [BASE_RECORD_ID]: recordId,
    [BASE_LEGACY_APP]: legacyApp,
    [BASE_UNIQUE_ID]: uniqueId,
    [IS_FROM_EPIC]: isFromEpic,
    [IS_FROM_ATHENA]: isFromAthena,
    [MODE]: mode,
    [BASE_SPECIMEN_NUMBER]: specimen_number,
    [BASE_SPECIMEN_NUMBER_ID]: specimen_number_id,
    [BASE_ACCESSION_NUMBER]:accessionNumber,
    [BASE_SPECIMEN_TYPE]: specimen_type
  });

  let submoduleurl = `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${params?.module_name}/${BASE_ROUTE_SUB_MODULE}/${data.url}?${PARAMS_KEY}=${paramsKey}`;

  if (menuItem) {
    submoduleurl += `&${BASE_MENU_ITEM}=${menuItem}`;
  }

  const isActive =
    location.pathname?.replace(/\/$/, '') ===
    `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${params?.module_name}/${BASE_ROUTE_SUB_MODULE}/${subListPath}`;

  const activeLink = useActiveLink(
    `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${params?.module_name}/${BASE_ROUTE_SUB_MODULE}/${data.url}`,
    !!data?.listMenuItems && data?.listMenuItems?.length > 0
  );

  const active = subListPath && url ? isActive : activeLink;

  const [openMenu, setOpenMenu] = useState(false);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);

  useEffect(() => {
    if (openMenu) {
      handleCloseMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpenMenu = useCallback(() => {
    if (data.listMenuItems && data?.listMenuItems?.length > 0) {
      setOpenMenu(true);
    }
  }, [data.listMenuItems]);

  return (
    <>
      <NavItem
        ref={navRef}
        open={openMenu}
        onMouseEnter={handleOpenMenu}
        onMouseLeave={handleCloseMenu}
        //
        name={data.name}
        url={submoduleurl}
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        //
        depth={depth}
        hasChild={data?.listMenuItems?.length > 0 && !!data.listMenuItems}
        externalLink={data.url.includes('http')}
        currentRole={slotProps?.currentRole}
        //
        active={active}
        className={active ? 'active' : ''}
        sx={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
        path={''}
      />

      {!!data?.listMenuItems && data?.listMenuItems?.length > 0 && (
        <Popover
          disableScrollLock
          open={openMenu}
          anchorEl={navRef.current}
          anchorOrigin={{vertical: 'center', horizontal: 'right'}}
          transformOrigin={{vertical: 'center', horizontal: 'left'}}
          slotProps={{
            paper: {
              onMouseEnter: handleOpenMenu,
              onMouseLeave: handleCloseMenu,
              sx: {
                mt: 0.5,
                minWidth: 160,
                ...(openMenu && {
                  pointerEvents: 'auto'
                })
              }
            }
          }}
          sx={{
            pointerEvents: 'none'
          }}>
          <NavSubList
            data={data.listMenuItems}
            depth={depth}
            slotProps={slotProps}
            url={data.url}
          />
        </Popover>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({data, depth, slotProps, url}: NavSubListProps) {
  return (
    <Stack spacing={0.5}>
      {data.map(list => (
        <SubModuleNavList
          key={list.name}
          data={list}
          depth={depth + 1}
          slotProps={slotProps}
          url={list.url}
          subListPath={url}
        />
      ))}
    </Stack>
  );
}
